/**
 * 숫자 표기 유틸.
 *
 * ⚠️ `options` 는 **선택 인자**다 — 필수로 좁히면 `fNumber(n)` 호출부가 전부 막힌다.
 */
import { formatNumberLocale } from 'src/locales';

const DEFAULT_LOCALE = { code: 'en-US', currency: 'USD' };

/** 호출부가 넘기는 것 — 숫자·문자열·빈 값을 다 받는다 */
type NumberInput = number | string | null | undefined;

function processInput(inputValue: NumberInput): number | null {
  if (inputValue == null || Number.isNaN(inputValue)) return null;
  return Number(inputValue);
}

// ----------------------------------------------------------------------

// 숫자 자리 별 , 포맷팅
/**
 * 숫자 자리 구분 표기 (예: 1,234.5)
 * @param inputValue
 * @param options
 * @returns 값이 없으면 빈 문자열
 */
export function fNumber(inputValue: NumberInput, options?: Intl.NumberFormatOptions): string {
  const locale = formatNumberLocale() || DEFAULT_LOCALE;

  const number = processInput(inputValue);
  if (number === null) return '';

  const fm = new Intl.NumberFormat(locale.code, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  }).format(number);

  return fm;
}

// ----------------------------------------------------------------------

/**
 * 통화 표기 — 로케일의 통화를 쓴다
 * @param inputValue
 * @param options
 * @returns 값이 없으면 빈 문자열
 */
export function fCurrency(inputValue: NumberInput, options?: Intl.NumberFormatOptions): string {
  const locale = formatNumberLocale() || DEFAULT_LOCALE;

  const number = processInput(inputValue);
  if (number === null) return '';

  const fm = new Intl.NumberFormat(locale.code, {
    style: 'currency',
    currency: locale.currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    ...options,
  }).format(number);

  return fm;
}

// ----------------------------------------------------------------------

/**
 * 퍼센트 표기 — **입력은 0~100 스케일**이다(내부에서 /100 한다)
 * @param inputValue
 * @param options
 * @returns 값이 없으면 빈 문자열
 */
export function fPercent(inputValue: NumberInput, options?: Intl.NumberFormatOptions): string {
  const locale = formatNumberLocale() || DEFAULT_LOCALE;

  const number = processInput(inputValue);
  if (number === null) return '';

  const fm = new Intl.NumberFormat(locale.code, {
    style: 'percent',
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
    ...options,
  }).format(number / 100);

  return fm;
}

// ----------------------------------------------------------------------

/**
 * 축약 표기 (예: 1.2k · 3.4m). 단위는 소문자로 낮춘다
 * @param inputValue
 * @param options
 * @returns 값이 없으면 빈 문자열
 */
export function fShortenNumber(inputValue: NumberInput, options?: Intl.NumberFormatOptions): string {
  const locale = formatNumberLocale() || DEFAULT_LOCALE;

  const number = processInput(inputValue);
  if (number === null) return '';

  const fm = new Intl.NumberFormat(locale.code, {
    notation: 'compact',
    maximumFractionDigits: 2,
    ...options,
  }).format(number);

  return fm.replace(/[A-Z]/g, (match) => match.toLowerCase());
}

// ----------------------------------------------------------------------

/**
 * 바이트 크기 표기 (예: 1.5 Kb)
 * @param inputValue
 * @returns
 */
export function fData(inputValue: NumberInput): string {
  const number = processInput(inputValue);
  if (number === null || number === 0) return '0 bytes';

  const units = ['bytes', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb', 'Eb', 'Zb', 'Yb'];
  const decimal = 2;
  const baseValue = 1024;

  const index = Math.floor(Math.log(number) / Math.log(baseValue));
  const fm = `${parseFloat((number / baseValue ** index).toFixed(decimal))} ${units[index]}`;

  return fm;
}
